import lodash from 'lodash';
import { assign } from 'xstate';
import { getPathToProperty } from '../metadata';
const { isEqual } = lodash;
const { set } = lodash;
const { unset } = lodash;
const { cloneDeep } = lodash;

export const mergeChanges = (machineContext, event) => {
    const payload = getChangePayload(event);
    // If a beginChange is called to set the form to dirty, but no actual
    // changes need be applied, then skip sending this to the rules engine.
    if (Object.keys(payload).length === 0 || !hasResolvableTarget(payload)) return;
    const {
        publish,
        newRecord,
        oldRecord,
        namespace,
        relation,
        type: machineContextType,
        formInstanceId,
        propertiesToUnset,
        hNode
    } = machineContext;
    const { originatingEventContext = {} } = event || {};
    const {
        type: originatingEventContextType,
        formInstanceId: originatingFormInstanceId
    } = originatingEventContext;
    // Keep the originating formInstanceId on the change event. A different form machine
    // can then ignore the response if rules processing finishes after another form opens.
    const { newRecord: optimisticNewRecord, propertiesToUnset: optimisticPropertiesToUnset } = getOptimisticChangeResult(
        { newRecord, propertiesToUnset },
        event
    );
    publish(
        { propertiesToUnset: optimisticPropertiesToUnset, oldRecord, newRecord: optimisticNewRecord, ...payload, hNode },
        {
            verb: 'change',
            namespace,
            relation,
            machineContextType,
            originatingEventContextType,
            type: originatingEventContextType ?? machineContextType,
            formInstanceId: originatingFormInstanceId ?? formInstanceId
        }
    );
};

export const optimisticallyMergeChangedRecord = assign((machineContext, event) => {
    return getOptimisticChangeResult(machineContext, event);
});

export const mergeChangedRecord = assign((machineContext, event) => {
    const { newRecord: prevRecord, propertiesToUnset: prevUnset } = machineContext;
    const { newRecord, propertiesToUnset } = event.payload;
    let merged = { ...prevRecord, ...newRecord };
    const newlyUnset = diff(prevUnset, propertiesToUnset) || [];
    merged = newlyUnset.reduce((result, propToUnset) => {
        const path = getPathToProperty(propToUnset);
        unset(result, path);
        return result;
    }, merged);
    return { newRecord: merged, propertiesToUnset };
});

export const raiseSuccess = (machineContext, event) => {
    const {
        publish,
        namespace,
        relation,
        newRecord,
        selectedTheme,
        hNode,
        type: machineContextType,
        formInstanceId: machineFormInstanceId
    } = machineContext;
    const { keepOpenAfterSubmit: _keepOpenAfterSubmit = false, originatingEventContext = {} } = event || {};
    const { type: originatingEventContextType, formInstanceId: originatingFormInstanceId } = originatingEventContext;
    // Include the saved record so form stays open with saved data
    const keepOpenAfterSubmit = _keepOpenAfterSubmit || (selectedTheme === 'gl2025' && hNode?.keepOpenAfterSubmit);
    const record = keepOpenAfterSubmit ? newRecord : undefined;
    // Copy the submit's formInstanceId to the success payload. Use this machine's ID
    // when the original submit event did not include the field.
    publish(
        {
            namespace,
            relation,
            type: originatingEventContextType ?? machineContextType,
            originatingEventContextType,
            machineContextType,
            record,
            keepOpenAfterSubmit,
            formInstanceId: originatingFormInstanceId ?? machineFormInstanceId
        },
        { verb: 'submit', namespace, relation, status: 'success' }
    );
};

export const updateContext = assign((machineContext, event) => {
    return event.payload.context;
});

export const update = (machineContext, event) => {
    const {
        newRecord,
        oldRecord,
        namespace,
        relation,
        publish,
        hNode,
        isNew,
        propertiesToUnset,
        routePath,
        type: machineContextType,
        formInstanceId
    } = machineContext;

    const { keepOpenAfterSubmit = false, originatingEventContext = {} } = event || {};
    const {
        type: originatingEventContextType,
        formInstanceId: originatingFormInstanceId
    } = originatingEventContext;

    publish(
        { newRecord, oldRecord, propertiesToUnset, keepOpenAfterSubmit },
        {
            verb: 'update',
            namespace,
            relation,
            type: originatingEventContextType ?? machineContextType,
            originatingEventContextType,
            machineContextType,
            formInstanceId: originatingFormInstanceId ?? formInstanceId,
            hNode,
            isNew,
            routePath
        }
    );
};

export const create = (
    {
        newRecord,
        oldRecord,
        namespace,
        relation,
        type,
        formInstanceId,
        publish,
        hNode,
        isNew,
        splashContext,
        routePath
    },
    event
) => {
    const originatingFormInstanceId = event?.originatingEventContext?.formInstanceId;
    publish(
        { newRecord, oldRecord, splashContext },
        {
            verb: 'create',
            namespace,
            relation,
            type,
            formInstanceId: originatingFormInstanceId ?? formInstanceId,
            hNode,
            isNew,
            routePath
        }
    );
};

export const validate = (machineContext, event) => {
    const {
        newRecord,
        oldRecord,
        namespace,
        relation,
        publish,
        hNode,
        isNew,
        routePath,
        type: machineContextType,
        formInstanceId
    } = machineContext;
    const { keepOpenAfterSubmit = false, originatingEventContext = {} } = event || {};
    const { type: originatingEventContextType, formInstanceId: originatingFormInstanceId } = originatingEventContext;
    publish(
        { newRecord, oldRecord, keepOpenAfterSubmit },
        {
            verb: 'validate',
            namespace,
            relation,
            type: originatingEventContextType ?? machineContextType,
            originatingEventContextType,
            machineContextType,
            formInstanceId: originatingFormInstanceId ?? formInstanceId,
            hNode,
            isNew,
            routePath
        }
    );
};


function diff(left, right) {
    if (!left?.length) return right;
    if (!right.length) return [];
    return right.filter(r => !left.some(l => isEqual(l, r)));
}

export function getOptimisticChangeResult(machineContext, event) {
    const { newRecord: previousRecord, propertiesToUnset: previousPropertiesToUnset = [] } = machineContext;
    const payload = getChangePayload(event);

    if (Object.keys(payload).length === 0 || !hasResolvableTarget(payload)) {
        return {
            newRecord: previousRecord,
            propertiesToUnset: previousPropertiesToUnset
        };
    }

    const {
        propertiesToUnset: payloadPropertiesToUnset = previousPropertiesToUnset,
        propertyPath = '',
        foreignNamespace,
        foreignRelation,
        propertyName,
        propertyOverride,
        newValue
    } = payload;

    const nextRecord = cloneDeep(previousRecord);
    const propertyKey = getPropertyKey({ propertyPath, foreignNamespace, foreignRelation, propertyName, propertyOverride });
    const nextPropertiesToUnset = payloadPropertiesToUnset.filter(key => !isEqual(propertyKey, key));
    const path = getPathToProperty({ propertyPath, foreignNamespace, foreignRelation, propertyName, propertyOverride });

    if (newValue === undefined || newValue === '') {
        nextPropertiesToUnset.push(propertyKey);
        unset(nextRecord, path);
    } else {
        set(nextRecord, path, newValue);
    }

    return {
        newRecord: nextRecord,
        propertiesToUnset: nextPropertiesToUnset
    };
}

function getPropertyKey({ propertyPath, foreignNamespace, foreignRelation, propertyName, propertyOverride }) {
    const propertyKey = { propertyPath, foreignNamespace, foreignRelation, propertyName };
    if (propertyOverride != null) {
        propertyKey.propertyOverride = propertyOverride;
    }
    return propertyKey;
}

function getChangePayload(event) {
    const rawPayload = event?.payload ?? {};
    // Internal event metadata should not be treated as change data.
    const payload = { ...rawPayload };
    delete payload.originatingEventContext;
    return payload;
}

function hasResolvableTarget({ propertyName, propertyOverride, foreignNamespace, foreignRelation }) {
    return (
        propertyOverride != null ||
        propertyName != null ||
        (foreignNamespace != null && foreignRelation != null)
    );
}

// remove the data that was saved by the removed hNode
export const removedHNode = (machineContext, event) => {
    const { publish, newRecord, oldRecord, namespace, relation, propertiesToUnset = [], type: machineContextType } = machineContext;
    const { payload,  originatingEventContext = {} } = event || {};
    const { hNode } = payload;   // this is the hNode that was removed
    const { type: originatingEventContextType, formInstanceId: originatingFormInstanceId } = originatingEventContext;
    // A NEW array rather than propertiesToUnset.push(...). This action is not an xstate `assign`,
    // so it cannot update the machine's context itself - the removal only reaches newRecord when
    // the change published below round-trips back as `changeSuccess`, and mergeChangedRecord
    // derives what to unset from diff(contextPropertiesToUnset, payloadPropertiesToUnset).
    // Pushing onto the context's own array put the entry on BOTH sides of that diff, so it was
    // never seen as newly unset, `unset` never ran, and the removed field stayed on the record.
    // Everything downstream reads the record, not propertiesToUnset - Save's change-set and, since
    // continuous scan, SessionStart's formMetadata - so removing a Mass Update row left the value
    // in place and the update went on applying a field the user had deleted (#1808).
    const nextPropertiesToUnset = [...propertiesToUnset, { propertyName: getPathToProperty(hNode) }];
    // event.payload is spread FIRST so the keys computed here always win. Spread last, a payload
    // carrying its own `propertiesToUnset` would silently replace the array built above and undo
    // this fix without a word. Harmless with today's single call site (DynamicFieldEntry publishes
    // `{ hNode }`), but the failure would be invisible the moment a second caller passed that key.
    publish(
        { ...event.payload, propertiesToUnset: nextPropertiesToUnset, oldRecord, newRecord, hNode },
        { verb: 'change', namespace, relation, type: originatingEventContextType ?? machineContextType,
            originatingEventContextType, machineContextType,
            formInstanceId: originatingFormInstanceId ?? machineContext.formInstanceId }
    );
};
